import { DELIVERY_LABEL, type Estimate, type Invoice } from "@clevis/shared-types";
import { cn } from "@clevis/shared-ui";
import { useMemberNames } from "../hooks/useMemberNames";

// Who did what to an estimate or invoice, oldest first: made, sent, emailed
// (and what happened to the email), signed, accepted or declined, deposits
// and payments, voided. Names come from the team list; Stripe and the
// maintenance-agreement run say so.

const money = (c: number) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
const when = (ms: number) => new Date(ms).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export function DocHistory({ tenantId, kind, doc }: { tenantId: string; kind: "estimate" | "invoice"; doc: (Estimate | Invoice) & { id: string } }) {
  const nameOf = useMemberNames(tenantId);
  const who = (uid?: string | null) =>
    !uid ? "" : uid === "stripe" ? "paid online (card)" : uid === "agreement" ? "the maintenance agreement" : uid === "customer" ? "the customer" : nameOf(uid);
  const events: Array<{ at: number; text: string; bad?: boolean }> = [];
  events.push({ at: doc.createdAt, text: `Made by ${who(doc.createdByUid) || "someone"}` });
  if (doc.sentAt) events.push({ at: doc.sentAt, text: `${kind === "invoice" ? "Finalized" : "Sent"}${doc.sentByUid ? ` by ${who(doc.sentByUid)}` : ""}` });
  for (const e of doc.emails ?? []) {
    const news = e.id ? doc.emailDelivery?.[e.id] : undefined;
    events.push({
      at: e.at,
      text: e.ok ? `Emailed by ${e.byName} to ${e.to.join(", ")}${e.docs?.length ? ` with ${e.docs.join(", ")}` : ""}${news ? `: ${DELIVERY_LABEL[news.status]}${news.detail ? ` (${news.detail})` : ""}` : ""}` : `Email by ${e.byName} didn't go out${e.error ? `: ${e.error}` : ""}`,
      bad: !e.ok || news?.status === "bounced" || news?.status === "complained",
    });
  }
  if (kind === "estimate") {
    const est = doc as Estimate;
    if (est.signedAt) events.push({ at: est.signedAt, text: `Signed by ${est.signerName ?? "the customer"} ${est.signedVia === "link" ? "online" : "on site"}` });
    else if (est.decidedAt && (est.status === "accepted" || est.status === "declined"))
      events.push({ at: est.decidedAt, text: `Marked ${est.status}${est.decidedByUid ? ` by ${who(est.decidedByUid)}` : est.status === "declined" ? " by the customer online" : ""}${est.declineNote ? `: “${est.declineNote}”` : ""}` });
    for (const p of est.depositPayments ?? []) events.push({ at: p.at, text: `Deposit ${money(p.amountCents)} (${p.method}) ${p.recordedByUid === "stripe" ? "paid online" : `recorded by ${who(p.recordedByUid)}`}${p.note ? ` · ${p.note}` : ""}` });
  } else {
    const inv = doc as Invoice;
    if (inv.signedAt) events.push({ at: inv.signedAt, text: `Signed by ${inv.signerName ?? "the customer"}${inv.signedByUid ? ` on ${who(inv.signedByUid)}'s device` : ""}` });
    for (const p of inv.payments ?? [])
      events.push({ at: p.at, text: `${p.deposit ? "Deposit" : "Payment"} ${money(p.amountCents)} (${p.method}) ${p.recordedByUid === "stripe" ? "paid online" : `recorded by ${who(p.recordedByUid)}`}${p.note ? ` · ${p.note}` : ""}` });
    if (inv.voidedAt) events.push({ at: inv.voidedAt, text: `Voided${inv.voidedByUid ? ` by ${who(inv.voidedByUid)}` : ""}`, bad: true });
  }
  events.sort((a, b) => a.at - b.at);
  return (
    <div className="mx-4 mb-2 rounded-lg bg-cream/50 px-3 py-1.5 text-xs">
      {events.map((e, i) => (
        <div key={i} className="flex gap-3 py-0.5">
          <span className="w-28 shrink-0 tabular-nums text-steel">{when(e.at)}</span>
          <span className={cn("min-w-0", e.bad ? "text-danger" : "text-ink")}>{e.text}</span>
        </div>
      ))}
    </div>
  );
}
